import * as React from "react"
import Link, { type LinkProps } from "next/link"
import type { VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import {
  buttonVariants,
  LINEAR_AI_BORDER_CLASSNAME,
  LINEAR_AI_CONTENT_CLASSNAME,
  LINEAR_AI_CONTENT_STYLE,
  getLinearAiContentSizeClassName,
} from "@/components/ui/button"
import { LinearAiBeam } from "@/components/ui/linear-ai-beam"

export type LinkButtonProps = React.PropsWithChildren<
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> &
  LinkProps &
  VariantProps<typeof buttonVariants> & {
    className?: string
    icon?: React.ReactNode
  }
>

function LinkButton({
  className,
  variant = "default",
  size = "default",
  icon,
  children,
  ...props
}: LinkButtonProps) {
  const isLinearAi = variant === "linear-ai"
  return (
    <Link
      data-slot="link-button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({ variant, size, className }),
        isLinearAi && "!p-0",
      )}
      {...props}
    >
      {isLinearAi ? (
        <>
          <span
            data-slot="linear-ai-border"
            className={LINEAR_AI_BORDER_CLASSNAME}
          />
          <LinearAiBeam />
          <span
            data-slot="linear-ai-content"
            className={cn(
              LINEAR_AI_CONTENT_CLASSNAME,
              getLinearAiContentSizeClassName(size),
            )}
            style={LINEAR_AI_CONTENT_STYLE}
          >
            {icon}
            {children}
          </span>
        </>
      ) : (
        <>
          {icon ? (
            <>
              {icon}
              {children}
            </>
          ) : (
            children
          )}

          <span
            data-slot="button-inset"
            className="absolute inset-0 pointer-events-none rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
          />
        </>
      )}
    </Link>
  )
}

export { LinkButton }
